import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { createSocketIOWithFailover } from '../utils/serverFailover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GamePlay({ user }) {
  const { pin } = useParams();
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState('waiting'); // waiting, playing, answered, leaderboard, finished
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [shuffledOptions, setShuffledOptions] = useState([]); // Opciones en orden aleatorio
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [score, setScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [answerResult, setAnswerResult] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Conectando...');
  const [connectionError, setConnectionError] = useState(null);
  const timerRef = useRef(null);
  const questionStartTime = useRef(null);

  // Función para mezclar array (Fisher-Yates shuffle)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    let mounted = true;
    let newSocket = null;

    const initializeConnection = async () => {
      try {
        setConnectionStatus('Conectando...');
        setConnectionError(null);

        newSocket = await createSocketIOWithFailover(
          io,
          user?.id || 'guest',
          (connectedSocket, server) => {
            // Connection successful
            if (!mounted) return;
            
            console.log(`Connected to game via ${server.name}`);
            setConnectionStatus(`Conectado a ${server.name}`);
            setSocket(connectedSocket);
            
            // Setup event listeners
            connectedSocket.on('connect', () => {
              console.log('Socket.IO connection established');
              connectedSocket.emit('join_session', { pin, user });
            });

            connectedSocket.on('game_started', () => {
              setGameState('waiting_question');
            });

            connectedSocket.on('new_question', (data) => {
              setCurrentQuestion(data);
              
              // Crear array con opciones y sus índices originales
              const optionsWithIndex = data.options.map((option, index) => ({
                text: option,
                originalIndex: index
              }));
              
              // Mezclar las opciones aleatoriamente
              const shuffled = shuffleArray(optionsWithIndex);
              setShuffledOptions(shuffled);
              
              setTimeLeft(data.time_limit);
              setSelectedAnswer(null);
              setAnswerResult(null);
              setGameState('playing');
              questionStartTime.current = Date.now();
            });

            connectedSocket.on('answer_result', (data) => {
              setAnswerResult(data);
              setScore(data.total_score);
              setGameState('answered');
            });

            connectedSocket.on('show_leaderboard', (data) => {
              setLeaderboard(data.leaderboard);
              setGameState('leaderboard');
            });

            connectedSocket.on('game_finished', (data) => {
              setLeaderboard(data.leaderboard);
              setGameState('finished');
            });

            connectedSocket.on('game_paused', () => {
              if (timerRef.current) clearInterval(timerRef.current);
              setGameState('paused');
            });

            connectedSocket.on('game_resumed', () => {
              if (gameState === 'paused' && currentQuestion) {
                setGameState('playing');
              }
            });

            // Emit join immediately if already connected
            if (connectedSocket.connected) {
              connectedSocket.emit('join_session', { pin, user });
            }
          },
          (error) => {
            console.error('Socket error:', error);
          },
          (status) => {
            if (mounted) {
              setConnectionStatus(status);
            }
          }
        );
      } catch (error) {
        console.error('Failed to connect:', error);
        if (mounted) {
          setConnectionError(error.message);
          setConnectionStatus('Error de conexión');
        }
      }
    };

    initializeConnection();

    return () => {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (newSocket) {
        newSocket.close();
      }
    };
  }, [pin, user]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, currentQuestion]);

  const handleAnswerSelect = (shuffledIndex) => {
    if (gameState !== 'playing' || selectedAnswer !== null) return;

    setSelectedAnswer(shuffledIndex);
    const timeTaken = (Date.now() - questionStartTime.current) / 1000;
    
    // Obtener el índice original de la opción seleccionada
    const originalIndex = shuffledOptions[shuffledIndex].originalIndex;

    if (socket) {
      socket.emit('submit_answer', {
        pin,
        answer: originalIndex, // Enviar el índice original al backend
        time_taken: timeTaken
      });
    }
  };

  const getOptionColors = () => {
    return [
      'bg-red-500 hover:bg-red-600',
      'bg-blue-500 hover:bg-blue-600',
      'bg-yellow-500 hover:bg-yellow-600',
      'bg-green-500 hover:bg-green-600'
    ];
  };

  const getOptionShapes = () => {
    return [
      '▲', // Triangle
      '◆', // Diamond
      '●', // Circle
      '■'  // Square
    ];
  };

  // Show connection error if exists
  if (connectionError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-900 flex items-center justify-center text-white p-4">
        <div className="text-center max-w-md">
          <div className="text-7xl mb-6">⚠️</div>
          <h1 className="text-3xl font-bold mb-4">Error de Conexión</h1>
          <p className="text-lg mb-6">{connectionError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-white text-red-600 rounded-lg font-bold hover:bg-red-50 transition"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'waiting' || gameState === 'waiting_question') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900 flex items-center justify-center text-white" data-testid="waiting-screen">
        <div className="text-center">
          {/* Connection status indicator */}
          <div className="mb-4 text-sm text-purple-200">
            {connectionStatus}
          </div>
          <div className="text-8xl mb-8 animate-bounce">🎮</div>
          <h1 className="text-4xl font-bold mb-4">
            {gameState === 'waiting' ? 'Esperando a que comience el quiz...' : 'Preparando siguiente pregunta...'}
          </h1>
          <div className="text-xl text-purple-200">¡Mantente listo!</div>
        </div>
      </div>
    );
  }

  if (gameState === 'paused') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-600 to-orange-600 flex items-center justify-center text-white" data-testid="paused-screen">
        <div className="text-center max-w-2xl mx-auto px-4">
          <div className="text-9xl mb-8 animate-pulse">⏸️</div>
          <h1 className="text-5xl font-bold mb-4">Juego Pausado</h1>
          <p className="text-2xl text-yellow-100 mb-6">
            El profesor ha pausado el juego
          </p>
          <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-xl p-6">
            <p className="text-lg">
              Espera a que el profesor reanude el juego para continuar
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'playing') {
    const colors = getOptionColors();
    const shapes = getOptionShapes();
    const timePercentage = (timeLeft / currentQuestion.time_limit) * 100;

    return (
      <div className="min-h-screen bg-gray-900 text-white" data-testid="playing-screen">
        {/* Timer Bar */}
        <div className="w-full h-4 bg-gray-800">
          <div
            className={`timer-bar h-full transition-all ${timePercentage > 50 ? 'bg-success' : timePercentage > 25 ? 'bg-warning' : 'bg-danger'}`}
            style={{ width: `${timePercentage}%` }}
          />
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="text-xl font-semibold">
              Pregunta {currentQuestion.question_number}/{currentQuestion.total_questions}
            </div>
            <div className="flex items-center gap-6">
              <div className="text-3xl font-bold">{timeLeft}s</div>
              <div className="text-xl">Puntos: {score}</div>
            </div>
          </div>

          {/* Question */}
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 mb-8 text-center">
            {currentQuestion.image_url && (
              <img 
                src={currentQuestion.image_url} 
                alt="Question" 
                className="max-h-64 mx-auto mb-6 rounded-lg shadow-xl"
              />
            )}
            <h2 className="text-3xl md:text-4xl font-bold" data-testid="question-text">{currentQuestion.question_text}</h2>
          </div>

          {/* Options - Orden aleatorio */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {shuffledOptions.map((option, shuffledIndex) => (
              <button
                key={shuffledIndex}
                onClick={() => handleAnswerSelect(shuffledIndex)}
                disabled={selectedAnswer !== null}
                className={`quiz-option ${colors[shuffledIndex]} text-white font-bold text-xl p-8 rounded-2xl shadow-xl transition-all transform ${
                  selectedAnswer === shuffledIndex ? 'selected scale-105 ring-4 ring-white' : ''
                } disabled:cursor-not-allowed`}
                data-testid={`option-btn-${shuffledIndex}`}
              >
                <div className="flex items-center justify-center gap-4">
                  <span className="text-4xl">{shapes[shuffledIndex]}</span>
                  <span>{option.text}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'answered') {
    const shapes = getOptionShapes(); // Define shapes for this section
    
    return (
      <div className={`min-h-screen flex items-center justify-center ${answerResult?.is_correct ? 'bg-success' : 'bg-danger'}`} data-testid="answer-result-screen">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <div className="text-9xl mb-8 animate-pulse">
            {answerResult?.is_correct ? '✓' : '✗'}
          </div>
          <h1 className="text-5xl font-bold mb-4">
            {answerResult?.is_correct ? '¡Correcto!' : 'Incorrecto'}
          </h1>
          {answerResult?.is_correct && (
            <div className="text-3xl mb-4">
              +{answerResult.points_earned} puntos
            </div>
          )}
          <div className="text-2xl mb-8">
            Puntuación total: {answerResult?.total_score}
          </div>

          {/* Show correct answer if user was wrong */}
          {!answerResult?.is_correct && currentQuestion && answerResult?.correct_answer !== undefined && (
            <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 mt-8">
              <div className="text-xl font-bold mb-4">💡 La respuesta correcta era:</div>
              <div className="bg-white bg-opacity-30 rounded-xl p-4">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl">{shapes[answerResult.correct_answer]}</span>
                  <span className="text-2xl font-bold">{currentQuestion.options[answerResult.correct_answer]}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (gameState === 'leaderboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900 text-white py-8" data-testid="leaderboard-screen">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-bold text-center mb-12">🏆 Clasificación</h1>
          <div className="space-y-4">
            {leaderboard.slice(0, 5).map((player, index) => (
              <div
                key={index}
                className={`bg-white bg-opacity-20 backdrop-blur-lg rounded-xl p-6 flex items-center gap-4 ${
                  player.name === user.name ? 'ring-4 ring-yellow-400' : ''
                }`}
                data-testid={`leaderboard-item-${index}`}
              >
                <div className={`text-4xl font-bold w-16 text-center ${
                  index === 0 ? 'text-yellow-400' : index === 1 ? 'text-gray-300' : index === 2 ? 'text-orange-400' : 'text-white'
                }`}>
                  {index + 1}
                </div>
                {player.picture && (
                  <img src={player.picture} alt={player.name} className="w-16 h-16 rounded-full" />
                )}
                <div className="flex-1">
                  <div className="font-bold text-xl">{player.name}</div>
                </div>
                <div className="text-2xl font-bold">{player.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'finished') {
    const myRank = leaderboard.findIndex(p => p.name === user.name) + 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary via-purple-700 to-pink-600 text-white py-8" data-testid="game-finished-screen">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold mb-4">¡Juego Terminado!</h1>
            {myRank <= 3 && (
              <div className="text-8xl mb-4 animate-bounce">
                {myRank === 1 ? '🥇' : myRank === 2 ? '🥈' : '🥉'}
              </div>
            )}
            <div className="text-3xl mb-2">Tu posición: #{myRank}</div>
            <div className="text-2xl text-purple-200">Puntuación final: {score}</div>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-center mb-8">Clasificación Final</h2>
            <div className="space-y-3">
              {leaderboard.map((player, index) => (
                <div
                  key={index}
                  className={`bg-white bg-opacity-20 rounded-xl p-4 flex items-center gap-4 ${
                    player.name === user.name ? 'ring-4 ring-yellow-400' : ''
                  }`}
                  data-testid={`final-leaderboard-item-${index}`}
                >
                  <div className={`text-2xl font-bold w-12 text-center ${
                    index === 0 ? 'text-yellow-400' : index === 1 ? 'text-gray-300' : index === 2 ? 'text-orange-400' : 'text-white'
                  }`}>
                    {player.rank}
                  </div>
                  {player.picture && (
                    <img src={player.picture} alt={player.name} className="w-12 h-12 rounded-full" />
                  )}
                  <div className="flex-1">
                    <div className="font-bold text-lg">{player.name}</div>
                  </div>
                  <div className="text-xl font-bold">{player.score}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default GamePlay;
